/**
 * Slice C.1 · Canonical Presentation Normaliser
 * ==============================================
 * Frontend-only normalisation boundary. Converts any shape returned
 * on `POST /api/universal_intent/session/{id}/turn` `dispatch_result`
 * into ONE small canonical presentation model consumed by the
 * renderers.
 *
 * Boundaries preserved:
 *   • No backend / adapter / manifest / classifier change.
 *   • All specialist-specific field detection lives here (not in the
 *     renderers) — swap detection here without touching UI files.
 *   • Missing / malformed structured fields FALL BACK to the plain
 *     narration renderer.
 *   • Never fabricates missing facts. If the server did not include
 *     an amount, the amount stays absent — we never invent one.
 *   • Prefers a future canonical `dispatch_result.presentation`
 *     object if present; otherwise reads today's field shapes.
 *
 * Precedence (highest → lowest):
 *   1. `dispatch_result.presentation`   — canonical future shape.
 *   2. specialist-specific detection    — today's shape by
 *                                          `env.specialist_id`.
 *   3. `dispatch_result.narration_hint` — server-authored one-liner.
 *   4. `env.narration`                  — orchestrator-level line.
 *   5. Fallback narration               — "I didn't understand …"
 *
 * Canonical model:
 *
 *     {
 *       kind:            "narration" | "booking-preview" |
 *                        "expense-preview" | "earnings-answer" |
 *                        "diary-answer"    | "help-answer" |
 *                        "navigate-choice" | "mail-answer",
 *       title:           string | null,
 *       narration:       string,          // always present
 *       data:            object | null,   // renderer payload
 *       primaryAction:   {label, kind, target?} | null,
 *       secondaryAction: {label, kind}                | null,
 *       specialistId:    string | null,
 *     }
 */

/**
 * Pure helper: return true if `value` contains any C0 control
 * character (U+0000..U+001F) or DEL (U+007F). Uses explicit
 * character-code logic — no regex — so nothing can be argued
 * about escape-sequence rendering.
 */
export function containsControlCharacter(value) {
  if (typeof value !== "string") return false;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x1F || code === 0x7F) return true;
  }
  return false;
}

const FALLBACK_NARRATION = "I didn't understand that yet.";
const RUN_DAY_INTENT_KEY = "run_my_day_requested";
const RUN_DAY_PACKAGE_KEY = "run_my_day_package";
const START_LESSON_INTENT_KEY = "start_lesson_requested";

/**
 * Complete the client half of the canonical Run My Day handoff.
 *
 * The specialist owns readiness and supplies the exact checked package. The
 * Today button historically wrote these two one-shot session keys before
 * opening Work Mode; voice/text Universal Intent must use the same contract.
 *
 * This boundary is shared by recorded voice, realtime voice and typed turns,
 * so one handoff keeps all three entry paths behaviourally identical.
 *
 * Returns true only after an authoritative ready package has been accepted.
 */
function _handoffReadyRunMyDay(dr) {
  const dayPackage = _asObject(dr?.day_package);
  const resumeActiveLesson =
    dr?.outcome === "started" && dr?.resume_active_lesson === true;
  const freshLaunch =
    ["ready", "started"].includes(dr?.outcome) &&
    dayPackage?.can_launch_work_mode === true;
  if (!resumeActiveLesson && !freshLaunch) {
    return false;
  }
  if (typeof window === "undefined") return false;

  try {
    if (freshLaunch) {
      window.sessionStorage.setItem(RUN_DAY_INTENT_KEY, "1");
      window.sessionStorage.setItem(RUN_DAY_PACKAGE_KEY, JSON.stringify(dayPackage));
      if (dr?.start_lesson_requested === true) {
        window.sessionStorage.setItem(START_LESSON_INTENT_KEY, "1");
      }
    }
  } catch (_) {
    // Work Mode can refetch the package when the launch flag is present. If
    // storage itself is unavailable we fail closed and leave the narration
    // visible rather than pretending the day started.
    return false;
  }

  // A full navigation tears down the live WebRTC surface. Work Mode either
  // consumes the fresh package or restores the durable active lesson itself.
  if (window.location?.pathname !== "/work-mode") {
    window.location.assign("/work-mode");
  }
  return true;
}

/**
 * Strict internal-route validation rule.
 *
 * A valid navigation target must:
 *   1. be a non-empty string;
 *   2. begin with exactly one forward slash;
 *   3. reject a second leading `/` or `\\`;
 *   4. reject every backslash anywhere in the value;
 *   5. reject control characters (incl. CR, LF, TAB);
 *   6. resolve against the current origin without changing origin;
 *   7. remain a relative internal application target;
 *   8. otherwise fall back to `NarrationBlock`.
 *
 * The confirm button is never rendered for an invalid target because
 * the `navigate-choice` kind is only emitted when this helper returns
 * `true`.
 */
export function isSafeInternalRoute(route, origin) {
  if (typeof route !== "string") return false;
  if (route.length === 0) return false;
  if (route.trim().length === 0) return false;
  // Must begin with exactly one forward slash …
  if (route.charCodeAt(0) !== 0x2F /* "/" */) return false;
  // … and the next char must NOT be a second slash or a backslash.
  const second = route.charAt(1);
  if (second === "/" || second === "\\") return false;
  // Reject any backslash anywhere.
  if (route.indexOf("\\") !== -1) return false;
  // Reject control characters (C0 range U+0000–U+001F, plus U+007F DEL)
  // via explicit character-code logic — no regex.
  if (containsControlCharacter(route)) return false;
  // Resolve against the current origin and prove same-origin.
  let base;
  if (typeof origin === "string" && origin) {
    base = origin;
  } else if (typeof window !== "undefined"
             && window.location
             && typeof window.location.origin === "string") {
    base = window.location.origin;
  } else {
    base = "http://internal.invalid";
  }
  try {
    const url = new URL(route, base);
    if (url.origin !== base) return false;
    if (!url.pathname.startsWith("/")) return false;
    return true;
  } catch (_) {
    return false;
  }
}

function _pickNarration(env, dr) {
  const hint = dr?.narration_hint;
  if (typeof hint === "string" && hint.trim()) return hint.trim();
  const nar = env?.narration;
  if (typeof nar === "string" && nar.trim()) return nar.trim();
  return FALLBACK_NARRATION;
}

function _asObject(x) {
  return (x && typeof x === "object" && !Array.isArray(x)) ? x : null;
}

/**
 * Normalise a Universal Intent turn envelope into the canonical
 * frontend presentation model.
 *
 * @param {object} env   the full HTTP envelope returned by the
 *                       Universal Intent turn endpoint.
 * @returns {object}     the canonical presentation model.
 */
export function normalizePresentation(env) {
  const safeEnv = _asObject(env) || {};
  const dr = _asObject(safeEnv.dispatch_result) || {};
  const specialistId = safeEnv.specialist_id || null;
  const narration = _pickNarration(safeEnv, dr);

  // Run My Day is an operation, not ordinary navigation. The specialist has
  // already checked preparation and supplies the exact checked package.
  if (specialistId === "ui_run_my_day" && _handoffReadyRunMyDay(dr)) {
    return {
      kind: "narration",
      title: null,
      narration,
      data: null,
      primaryAction: null,
      secondaryAction: null,
      specialistId,
    };
  }

  // 1 · future canonical shape wins if provided.
  const canonical = _asObject(dr.presentation);
  if (canonical && typeof canonical.kind === "string") {
    const primary   = _asObject(canonical.primary_action)   || _asObject(canonical.primaryAction);
    const secondary = _asObject(canonical.secondary_action) || _asObject(canonical.secondaryAction);
    return {
      kind:            canonical.kind,
      title:           canonical.title || null,
      narration:       (typeof canonical.narration === "string" && canonical.narration.trim())
                          ? canonical.narration.trim() : narration,
      data:            _asObject(canonical.data),
      primaryAction:   primary,
      secondaryAction: secondary,
      specialistId,
    };
  }

  // 2 · today's per-specialist shapes.
  switch (specialistId) {
    case "ui_uc_report": {
      const evidence = _asObject(dr.uc_evidence);
      if (dr.private_uc === true && dr.access === "read_only" && evidence?.kind === "payroll_search"
          && typeof evidence.owner_id === "string" && Number.isSafeInteger(evidence.setup_revision)
          && evidence.setup_revision >= 0 && /^[a-f0-9]{64}$/.test(evidence.source_snapshot_sha256 || "")) {
        return { kind: "uc-report", title: "Find your UC payslip", narration,
          data: evidence, primaryAction: null, secondaryAction: null, specialistId };
      }
      const report = _asObject(dr.uc_report);
      if (dr.private_uc === true && dr.access === "read_only" && report
          && /^[a-f0-9]{64}$/.test(report.id || "")
          && /^[a-f0-9]{64}$/.test(report.report_sha256 || "")
          && typeof report.owner_id === "string") {
        return { kind: "uc-report", title: "Your Universal Credit report", narration,
          data: report, primaryAction: null, secondaryAction: null, specialistId };
      }
      break;
    }
    case "ui_icloud_mail": {
      const mail = _asObject(dr.mail_data);
      if (dr.private_mail === true && dr.access === "read_only" && mail) {
        return {
          kind: "mail-answer", title: "Your iCloud email", narration,
          data: mail, primaryAction: null, secondaryAction: null, specialistId,
        };
      }
      break;
    }
    case "ui_booking_request": {
      const bp = _asObject(dr.booking_preview);
      if (bp) return {
        kind: "booking-preview",
        title: "Booking preview",
        narration,
        data: bp,
        primaryAction: null,
        secondaryAction: null,
        specialistId,
      };
      break;
    }
    case "ui_expense_recorder": {
      const ep = _asObject(dr.expense_preview);
      if (ep) return {
        kind: "expense-preview",
        title: "Expense recorded",
        narration,
        data: ep,
        primaryAction: null,
        secondaryAction: null,
        specialistId,
      };
      break;
    }
    case "ui_earnings_query": {
      const ea = _asObject(dr.earnings_answer);
      if (ea) return {
        kind: "earnings-answer",
        title: "Earnings",
        narration,
        data: ea,
        primaryAction: null,
        secondaryAction: null,
        specialistId,
      };
      break;
    }
    case "ui_diary_query": {
      const da = _asObject(dr.diary_answer);
      if (da) return {
        kind: "diary-answer",
        title: "Diary",
        narration,
        data: da,
        primaryAction: null,
        secondaryAction: null,
        specialistId,
      };
      break;
    }
    case "ui_help_lookup": {
      const ha = _asObject(dr.help_snippet);
      if (ha) return {
        kind: "help-answer",
        title: "Help",
        narration,
        data: ha,
        primaryAction: null,
        secondaryAction: null,
        specialistId,
      };
      break;
    }
    case "ui_navigate_to": {
      const route = _asObject(dr.navigation)?.route;
      if (isSafeInternalRoute(route)) {
        return {
          kind: "navigate-choice",
          title: "Take you there?",
          narration,
          data: { route },
          primaryAction:   { kind: "navigate", label: "Take me there", target: route },
          secondaryAction: { kind: "dismiss",  label: "Not now" },
          specialistId,
        };
      }
      break;
    }
    default: break;
  }

  // 3 · fallback narration.
  return {
    kind: "narration",
    title: null,
    narration,
    data: null,
    primaryAction: null,
    secondaryAction: null,
    specialistId,
  };
}

export const _test_ = { FALLBACK_NARRATION };

